import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Siren } from "lucide-react";
import { scrollToId, IMAGES, PHONE_TEL, PHONE2_TEL, WHATSAPP_URL } from "@/lib/site";

const LINES = ["PRECISION", "IS NOT A", "PROMISE."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-screen items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-24">
        <img src={IMAGES.hero} alt="Sagittarius Motor Works workshop" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10] via-transparent to-[#0A0C10]/60" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-8 lg:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-amber-500"
          data-testid="hero-eyebrow"
        >
          Est. 2025 — Independent Car Care & BMW Specialists
        </motion.p>

        <p className="font-display leading-[0.9] tracking-tight" data-testid="hero-slogan">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className={`block text-6xl uppercase sm:text-7xl lg:text-[7.5rem] ${
                  i === 2 ? "text-amber-500" : "text-slate-50"
                }`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </p>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.6 }}
          className="mt-8 max-w-3xl text-2xl font-bold leading-snug text-slate-50 sm:text-3xl"
          data-testid="hero-headline"
        >
          Mechanic &amp; BMW Specialist in Mount Maunganui
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6 }}
          className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300"
          data-testid="hero-sub"
        >
          Sagittarius Motor Works is an independent car repair workshop at Unit 1, 9 Cherokee Place, Mount Maunganui,
          looking after drivers across Mount Maunganui and Tauranga. We service and repair all kinds of cars, and we
          specialise in BMW: dealer-level diagnostics, coding and programming, and Japanese-import navigation conversions.
          Open 7 days, 8:00am–5:30pm, with 24/7 breakdown help. Call{" "}
          <a href={PHONE_TEL} className="text-amber-500 hover:underline">022 566 5221</a> or{" "}
          <a href={PHONE2_TEL} className="text-amber-500 hover:underline">027 362 6970</a>, or{" "}
          <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="text-amber-500 hover:underline">message us on WhatsApp</a>.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("booking")}
            data-testid="hero-book-cta"
            className="group flex items-center gap-3 bg-amber-600 px-8 py-4 font-mono text-sm uppercase tracking-[0.2em] text-black transition-colors duration-200 hover:bg-amber-500"
          >
            Book a Service
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollToId("booking")}
            data-testid="hero-emergency-cta"
            className="flex items-center gap-3 border border-red-500/60 bg-red-500/10 px-8 py-4 font-mono text-sm uppercase tracking-[0.2em] text-red-400 transition-colors duration-200 hover:bg-red-500 hover:text-white"
          >
            <Siren size={16} className="animate-pulse-glow" />
            Emergency Breakdown
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-16 grid grid-cols-2 divide-x divide-white/10 border-y border-white/10"
          data-testid="hero-stats"
        >
          {[
            ["2025", "Est. Mount Maunganui"],
            ["24/7", "Recovery response"],
          ].map(([v, l]) => (
            <div key={l} className="px-4 py-5 sm:px-8">
              <div className="font-display text-3xl text-slate-50 sm:text-4xl">{v}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 sm:text-xs">{l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
